import React, { useContext, useEffect, useMemo, useState } from 'react';
import ProjectContainer from '../../../components/containers/ProjectContainer';
import { AppSettingsContext } from '../../../providers/AppSettingsProvider/context';
import { Components } from './styled';
import ProjectTextField from '../../../components/inputs/ProjectTextField';
import { ToastContext } from '../../../providers/ToastProvider/context';
import { getProjectErrorForCode, isProjectError } from '../../../utils/errors';
import { ProjectErrors, ProjectErrorType } from '../../../utils/errors/types';
import {
  copyUserData,
  deleteProfileSubCollectionDocs,
  deleteProfileSurveyAndLearningPathFields,
  getProfileByUserId,
  getProfileTransactionsAndPurchases,
  updateProfile,
  updatePromoCodeData,
} from '../../../api/profile';
import { Profile, ProfileTransactionsAndPurchases, Purchase, Transaction } from '../../../api/types/profile';
import { useNavigate, useParams } from 'react-router-dom';
import UserProfileField from '../../../components/UserProfileField';
import { UserProfileFields, UserProfileFieldTypes } from '../../../components/UserProfileField/types';
import { preprocessUserProfileForLookupPage } from '../utils';
import { ReactComponent as AccessLinkIcon } from '../../../assets/icons/icon-grey-check-square-offset.svg';
import { USER_LINKS } from '../../../defines';
import useFormTextState from '../../../hooks/useFormTextState';
import { Deal } from '../../../api/types/others';
import { ActivityIndicatorContext } from '../../../providers/ActivityIndicatorProvider/context';
import { CreateProfileDto } from '../../../api/typeDtos/profile';
import ProjectTable from '../../../components/ProjectTable';
import { tableColumns } from '../../../components/ProjectTable/constants';
import useModalState from '../../../hooks/useModalState';
import CreateDealModal from '../../../components/modals/CreateDealModal';
import { SessionContext } from '../../../providers/SessionProvider/context';
import _ from 'lodash';
import {
  deleteAccountOnAdmin,
  getRevenueCatUser,
  getUserByEmail,
  impersonateUser,
  recoverUserSubscriptionIntervals,
} from '../../../api/user';
import { confirmationTexts } from '../../../components/modals/ConfirmationModal/utils';
import ConfirmationModal from '../../../components/modals/ConfirmationModal';
import SubscriptionInfoModal from './components/SubscriptionInfoModal';
import { isValidEmail } from '../../../utils/validation';
import { sendPasswordResetEmailToUser } from '../../../api/auth';

const UserLookup = () => {
  const navigate = useNavigate();
  const { id: initialId } = useParams<{ id: string }>();

  const { collectionContentGroups } = useContext(AppSettingsContext);
  const { showErrorToast, showSuccessToast, showWarningToast } = useContext(ToastContext);
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { currentProfile, refreshProfile } = useContext(SessionContext);

  const [user, setUser] = useState<Profile | undefined>();
  const [userFields, setUserFields] = useState<UserProfileFields | undefined>();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [purchases, setPurchases] = useState<Purchase[]>([]);

  const [admin, setAdmin] = useState<boolean | undefined>();
  const [tester, setTester] = useState<boolean | undefined>();

  const [gifts, setGifts] = useState<Deal[]>([]);
  const [initialGifts, setInitialGifts] = useState<Deal[]>([]);

  const [initialSurvey, setInitialSurvey] = useState<string | undefined>();
  const [learningPaths, setLearningPaths] = useState<string[]>([]);

  const userIdentifierProps = useFormTextState(initialId || '');
  const forcedAliasProps = useFormTextState('');
  const copyUserDataTargetIdProps = useFormTextState('');
  const passwordResetEmailProps = useFormTextState('');

  const subscriptionInfoModal = useModalState<any>();

  const confirmationModal = useModalState<boolean>();
  const confirmationModalDeleteAccount = useModalState<boolean>();
  const confirmationModalImpersonateUser = useModalState<boolean>();
  const confirmationModalDeleteSurveysAndLearningPaths = useModalState<boolean>();
  const confirmationModalCopyUserData = useModalState<{ sourceProfile?: Profile; targetProfile: Profile }>();
  const confirmationModalSendPasswordResetEmail = useModalState<{ email: string; userId?: string }>();

  const createDealModalProps = useModalState<{
    dealType: 'gift';
    deals: Deal[];
    setDeals?: React.Dispatch<React.SetStateAction<Deal[]>>;
  }>();

  useEffect(() => {
    if (!!user) {
      setUserFields(preprocessUserProfileForLookupPage(user));
      setAdmin(user?.isAdmin);
      setTester(user?.isTester);

      setGifts(user?.gifts || []);
      setInitialGifts(user?.gifts || []);

      setInitialSurvey(user?.initialSurvey);
      setLearningPaths(user?.learningPaths || []);

      forcedAliasProps.setValue(user?.forcedAlias);
    }
  }, [user]);

  useEffect(() => {
    userIdentifierProps.setValue(initialId);
    setUser(undefined);
    setTransactions([]);
    setPurchases([]);
    setUserFields(undefined);
    fetchUserProfile().then();
  }, [initialId]);

  const refreshData = async () => {
    if (!initialId) {
      return undefined;
    }
    const result = await getProfileByUserId(initialId, false);
    if (isProjectError(result) || !result) {
      return undefined;
    }
    setUser(result as Profile);
    const resultTransactionsAndPurchases = await getProfileTransactionsAndPurchases(initialId);
    if (isProjectError(resultTransactionsAndPurchases)) {
      return undefined;
    }
    const { transactions, purchases } = resultTransactionsAndPurchases as ProfileTransactionsAndPurchases;
    setTransactions(transactions);
    setPurchases(purchases);
  };

  const fetchUserProfile = async () => {
    if (!initialId || initialId?.length === 0) {
      showWarningToast('Please enter a user ID');
      return undefined;
    }
    const result = await getProfileByUserId(initialId, false);
    if (!result) {
      showErrorToast(getProjectErrorForCode(ProjectErrors.noUserFoundWithID));
      return undefined;
    }
    if (isProjectError(result)) {
      showErrorToast(result as ProjectErrorType);
      return undefined;
    }
    showSuccessToast('User found!');
    setUser(result as Profile);
    const resultTransactionsAndPurchases = await getProfileTransactionsAndPurchases(initialId);
    if (isProjectError(resultTransactionsAndPurchases)) {
      showErrorToast(resultTransactionsAndPurchases as ProjectErrorType);
      return undefined;
    }
    const { transactions, purchases } = resultTransactionsAndPurchases as ProfileTransactionsAndPurchases;
    setTransactions(transactions);
    setPurchases(purchases);
  };

  const onSearchByID = (id: string | undefined) => {
    navigate(`/user-lookup/${id}`);
  };

  const _onSearch = async () => {
    const queryIsEmail = isValidEmail(userIdentifierProps.value);
    if (userIdentifierProps.value && queryIsEmail) {
      setIsLoading(true);
      const result = await getUserByEmail(userIdentifierProps.value);
      setIsLoading(false);
      if (result && result?.length) {
        onSearchByID(result);
      } else {
        showErrorToast(getProjectErrorForCode(ProjectErrors.noUserFoundWithEmail));
      }
    } else {
      onSearchByID(userIdentifierProps.value);
    }
  };

  const onClickAdmin = (type: 'grant' | 'revoke') => {
    if (currentProfile && currentProfile?.isSuperAdmin) {
      switch (type) {
        case 'grant':
          setAdmin(true);
          break;
        case 'revoke':
          setAdmin(false);
          break;
      }
    } else {
      showErrorToast(getProjectErrorForCode(ProjectErrors.superUserPermissions));
    }
  };
  const onClickTester = (type: 'grant' | 'revoke') => {
    switch (type) {
      case 'grant':
        setTester(true);
        break;
      case 'revoke':
        setTester(false);
        break;
    }
  };

  const onVerifyWithRevenueCat = async () => {
    if (initialId) {
      setIsLoading(true);
      const result = await getRevenueCatUser(initialId);
      setIsLoading(false);
      subscriptionInfoModal.open(result);
    }
  };
  const onConfirmForceSync = () => {
    confirmationModal.open(true);
  };
  const onForceSyncWithRevenueCat = async () => {
    if (currentProfile?.isSuperAdmin) {
      if (initialId) {
        setIsLoading(true);
        const result = await recoverUserSubscriptionIntervals(initialId);
        setIsLoading(false);
        const isSuccessful = !!result?.success;
        const hasErrors = !!result?.errorDetails;
        if (isSuccessful) {
          showSuccessToast('The sync has been done successfully');
          await refreshData();
        } else if (hasErrors) {
          // @ts-ignore
          showErrorToast(ProjectErrors[result?.errorDetails] || ProjectErrors.genericTryAgain);
        }
      }
    } else {
      showWarningToast(getProjectErrorForCode(ProjectErrors.userIsNotSuperUser));
    }
  };

  const onConfirmDeleteAccount = () => {
    confirmationModalDeleteAccount.open(true);
  };
  const onDeleteAccount = async () => {
    if (initialId) {
      setIsLoading(true);
      const paymentInfo = await getRevenueCatUser(initialId);
      const result = await deleteAccountOnAdmin(initialId, paymentInfo);
      setIsLoading(false);
      const isSuccessful = !!result?.success;
      if (isSuccessful) {
        showSuccessToast('The account was deleted successfully');
        navigate('/user-lookup');
      } else {
        // @ts-ignore
        showErrorToast(result?.message || ProjectErrors.genericTryAgain);
      }
    }
  };

  const onConfirmDeleteSurveysAndLearningPaths = () => {
    confirmationModalDeleteSurveysAndLearningPaths.open(true);
  };
  const onDeleteSurveysAndLearningPaths = async () => {
    if (currentProfile?.isSuperAdmin) {
      if (initialId) {
        setIsLoading(true);
        let result = await deleteProfileSurveyAndLearningPathFields(initialId);
        let resultError = isProjectError(result);
        if (resultError) {
          setIsLoading(false);
          showErrorToast(resultError);
          return;
        }
        result = await deleteProfileSubCollectionDocs({ profileId: initialId, subCollectionName: 'surveys' });
        resultError = isProjectError(result);
        if (resultError) {
          setIsLoading(false);
          showErrorToast(resultError);
          return;
        }
        let successResult = result as { success: boolean; count: number };
        const deletedSurveysCount = successResult?.success && successResult?.count ? successResult?.count : 0;
        result = await deleteProfileSubCollectionDocs({ profileId: initialId, subCollectionName: 'learningPaths' });
        resultError = isProjectError(result);
        if (resultError) {
          setIsLoading(false);
          showErrorToast(resultError);
          return;
        }
        successResult = result as { success: boolean; count: number };
        const deletedLearningPathsCount = successResult?.success && successResult?.count ? successResult?.count : 0;
        setIsLoading(false);
        if (deletedSurveysCount === 0 && deletedLearningPathsCount === 0) {
          showSuccessToast("There aren't any surveys or learning paths on this profile.");
        } else if (deletedSurveysCount && deletedLearningPathsCount) {
          showSuccessToast(
            `Successfully deleted ${deletedSurveysCount} survey(s) and ${deletedLearningPathsCount} learning path(s).`,
          );
        } else {
          showSuccessToast(
            `Successfully deleted ${deletedSurveysCount || deletedLearningPathsCount} 
          ${deletedSurveysCount ? 'survey(s).' : 'learning path(s).'}`,
          );
        }
        await refreshData();
      }
    } else {
      showWarningToast(getProjectErrorForCode(ProjectErrors.userIsNotSuperUser));
    }
  };

  const onResetSurveys = () => {
    setInitialSurvey('');
  };
  const onResetLearningPaths = () => {
    setLearningPaths([]);
  };

  function onClickAddGift() {
    createDealModalProps.open({ dealType: 'gift', deals: gifts, setDeals: setGifts });
  }

  function onRemoveGift(rowId: string) {
    const newGifts = [...gifts];
    newGifts.splice(parseInt(rowId), 1);
    setGifts(newGifts);
  }

  const _onSave = async () => {
    setIsLoading(true);

    const addedGifts = _.differenceWith(gifts, initialGifts, _.isEqual);
    const remainingGifts = _.differenceWith(gifts, addedGifts, _.isEqual);

    const data: CreateProfileDto = {
      isAdmin: admin || false,
      isTester: tester || false,
      ...(!!forcedAliasProps?.value && forcedAliasProps?.value?.length > 0 && { forcedAlias: forcedAliasProps?.value }),
      gifts: remainingGifts,
      initialSurvey,
      learningPaths,
    };

    // UpdateProfile should be called before updatePromoCodeData
    const result = initialId && (await updateProfile({ ...data, id: initialId }));

    if (addedGifts?.length && initialId) {
      await updatePromoCodeData({ profileId: initialId, gifts: addedGifts });
    }

    const resultError = isProjectError(result);
    if (resultError) {
      showErrorToast(resultError);
    } else {
      showSuccessToast('The changes were saved successfully');
      await refreshData();
    }
    setIsLoading(false);
  };

  const getNameForGiftContext = (item?: string) => {
    if (item === 'monthly_subscription') {
      return 'Monthly subscription';
    } else if (item === 'yearly_subscription') {
      return 'Yearly subscription';
    } else if (item?.startsWith('com.alux.sub.monthly')) {
      return 'Monthly subscription (deprecated)';
    } else if (item?.startsWith('com.alux.sub.yearly')) {
      return 'Yearly subscription (deprecated)';
    }
    const foundCollection = collectionContentGroups.find((t) => t.id === item);
    if (foundCollection) {
      return foundCollection.title;
    }
    return item ? `${item} (deprecated)` : 'Unknown';
  };

  const giftsWithContextNames = useMemo(
    () => (gifts || []).map((t) => (t.context ? { ...t, context: getNameForGiftContext(t.context) } : t)),
    [gifts, user],
  );

  const onConfirmImpersonateUser = () => {
    confirmationModalImpersonateUser.open(true);
  };

  const onImpersonateUser = async () => {
    if (initialId) {
      setIsLoading(true);
      const result = await impersonateUser({ userId: initialId });
      setIsLoading(false);
      const isSuccessful = !!result?.success;
      if (isSuccessful) {
        showSuccessToast('The impersonation was successful');
        refreshProfile().then();
      } else {
        // @ts-ignore
        showErrorToast(result?.getUserMessage?.() || result?.message || ProjectErrors.genericTryAgain);
      }
    }
  };

  const onConfirmCopyUserData = async () => {
    const query = copyUserDataTargetIdProps.value?.trim();
    if (!query) {
      showWarningToast('Please enter the target user ID or email');
      return;
    }

    setIsLoading(true);
    let targetUserId = query;
    if (isValidEmail(query)) {
      const foundId = await getUserByEmail(query);
      if (!foundId || typeof foundId !== 'string') {
        setIsLoading(false);
        showErrorToast(getProjectErrorForCode(ProjectErrors.noUserFoundWithEmail));
        return;
      }
      targetUserId = foundId;
    }

    if (targetUserId === initialId) {
      setIsLoading(false);
      showWarningToast('The target user cannot be the same as the current user');
      return;
    }

    const targetProfileResult = await getProfileByUserId(targetUserId, false);
    setIsLoading(false);
    if (!targetProfileResult || isProjectError(targetProfileResult)) {
      showErrorToast(getProjectErrorForCode(ProjectErrors.noUserFoundWithID));
      return;
    }

    confirmationModalCopyUserData.open({ sourceProfile: user, targetProfile: targetProfileResult as Profile });
  };

  const onCopyUserData = async (modalState?: { sourceProfile?: Profile; targetProfile: Profile }) => {
    const targetProfile = modalState?.targetProfile;
    if (initialId && targetProfile?.id) {
      setIsLoading(true);
      const result = await copyUserData(initialId, targetProfile.id);
      setIsLoading(false);
      const resultError = isProjectError(result);
      const isSuccessful = !resultError && !!result?.data?.success;
      if (isSuccessful) {
        showSuccessToast('The user data was copied successfully');
        copyUserDataTargetIdProps.setValue('');
      } else {
        showErrorToast(
          resultError ? (result as ProjectErrorType) : getProjectErrorForCode(ProjectErrors.genericTryAgain),
        );
      }
    }
  };

  const onConfirmSendPasswordResetEmail = async () => {
    const email = passwordResetEmailProps.value?.trim();
    if (!email || !isValidEmail(email)) {
      showWarningToast('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    const foundId = await getUserByEmail(email);
    setIsLoading(false);
    if (!foundId || typeof foundId !== 'string') {
      showErrorToast(getProjectErrorForCode(ProjectErrors.noUserFoundWithEmail));
      return;
    }
    if (foundId !== initialId) {
      showErrorToast(getProjectErrorForCode(ProjectErrors.emailBelongsToDifferentAccount));
      return;
    }

    confirmationModalSendPasswordResetEmail.open({ email, userId: initialId });
  };

  const onSendPasswordResetEmail = async (modalState?: { email: string; userId?: string }) => {
    if (modalState?.email) {
      if (modalState.userId !== initialId) {
        showErrorToast(getProjectErrorForCode(ProjectErrors.displayedUserChanged));
        return;
      }
      setIsLoading(true);
      const result = await sendPasswordResetEmailToUser(modalState.email);
      setIsLoading(false);
      const resultError = isProjectError(result);
      if (resultError) {
        showErrorToast(resultError);
      } else {
        showSuccessToast('Password reset email sent');
        passwordResetEmailProps.setValue('');
      }
    }
  };

  return (
    <ProjectContainer title={'User Lookup'}>
      <Components.Container>
        <Components.SearchHeader>
          <Components.FieldWrapper>
            <Components.FieldLabel>Search by user ID or email</Components.FieldLabel>
            <Components.SearchUserWrapper>
              <ProjectTextField submitOnEnter={_onSearch} {...userIdentifierProps} placeholder={'Enter ID or email'} />
              <Components.SearchButton onClick={_onSearch}>Search</Components.SearchButton>
            </Components.SearchUserWrapper>
          </Components.FieldWrapper>
        </Components.SearchHeader>

        {!!user && (
          <>
            <UserProfileField
              displayName={'Full name'}
              fieldType={UserProfileFieldTypes.text}
              data={`${user?.firstName} ${user?.lastName}`}
            />

            <Components.FieldWrapper>
              <Components.FieldLabel>Access user info on other platforms</Components.FieldLabel>
              <>
                <Components.LinkAccessWrapper>
                  <Components.LinkLabel>Firestore</Components.LinkLabel>
                  <Components.IconWrapper
                    onClick={() => {
                      window.open(USER_LINKS.firestore(initialId), '_blank');
                    }}>
                    <AccessLinkIcon />
                  </Components.IconWrapper>
                </Components.LinkAccessWrapper>

                <Components.LinkAccessWrapper>
                  <Components.LinkLabel>Customer IO</Components.LinkLabel>
                  <Components.IconWrapper
                    onClick={() => {
                      window.open(USER_LINKS.customerIo(initialId), '_blank');
                    }}>
                    <AccessLinkIcon />
                  </Components.IconWrapper>
                </Components.LinkAccessWrapper>

                <Components.LinkAccessWrapper>
                  <Components.LinkLabel>Mixpanel</Components.LinkLabel>
                  <Components.IconWrapper
                    onClick={() => {
                      window.open(USER_LINKS.mixpanel(initialId), '_blank');
                    }}>
                    <AccessLinkIcon />
                  </Components.IconWrapper>
                </Components.LinkAccessWrapper>

                <Components.LinkAccessWrapper>
                  <Components.LinkLabel>RevenueCat</Components.LinkLabel>
                  <Components.IconWrapper
                    onClick={() => {
                      window.open(USER_LINKS.revenueCat(initialId), '_blank');
                    }}>
                    <AccessLinkIcon />
                  </Components.IconWrapper>
                </Components.LinkAccessWrapper>
              </>
            </Components.FieldWrapper>

            {user?.id !== currentProfile?.id ? (
              <Components.FieldWrapper>
                <Components.FieldLabel>Impersonation</Components.FieldLabel>
                <Components.ImpersonateButton onClick={onConfirmImpersonateUser}>
                  Impersonate user
                </Components.ImpersonateButton>
              </Components.FieldWrapper>
            ) : null}

            <Components.FieldWrapper>
              <Components.FieldLabel>Copy user data to another account</Components.FieldLabel>
              <ProjectTextField
                submitOnEnter={onConfirmCopyUserData}
                {...copyUserDataTargetIdProps}
                placeholder={'Enter the target user ID or email'}
              />
              <Components.SecondaryButton large={true} onClick={onConfirmCopyUserData}>
                Copy data to this user
              </Components.SecondaryButton>
            </Components.FieldWrapper>

            <Components.FieldWrapper>
              <Components.FieldLabel>Send password reset email</Components.FieldLabel>
              <ProjectTextField
                submitOnEnter={onConfirmSendPasswordResetEmail}
                {...passwordResetEmailProps}
                placeholder={"Enter the user's account email"}
              />
              <Components.SecondaryButton large={true} onClick={onConfirmSendPasswordResetEmail}>
                Send password reset email
              </Components.SecondaryButton>
            </Components.FieldWrapper>

            <Components.FieldWrapper>
              <Components.FieldLabel>Verify / update subscription data</Components.FieldLabel>
              <>
                <Components.SecondaryButton large={true} onClick={onVerifyWithRevenueCat}>
                  Verify RevenueCat subscription
                </Components.SecondaryButton>

                <Components.SecondaryButton large={true} onClick={onConfirmForceSync}>
                  Force sync with RevenueCat
                </Components.SecondaryButton>
              </>
            </Components.FieldWrapper>

            <Components.FieldWrapper>
              <Components.FieldLabel danger={true}>Danger zone</Components.FieldLabel>
              <Components.SecondaryButton danger={true} large={true} onClick={onConfirmDeleteAccount}>
                Delete account
              </Components.SecondaryButton>
              <Components.SecondaryButton danger={true} large={true} onClick={onConfirmDeleteSurveysAndLearningPaths}>
                Delete Surveys and Learning Paths
              </Components.SecondaryButton>
            </Components.FieldWrapper>

            <Components.Separator />
            <Components.Header>Editable profile attributes</Components.Header>

            <Components.FieldWrapper>
              <Components.SubsectionTitle>Special tags</Components.SubsectionTitle>
              <>
                <Components.CheckboxFieldWrapper>
                  <Components.Checkbox checked={admin || false} disabled />
                  <Components.CheckboxLabel>Admin</Components.CheckboxLabel>
                  <Components.SecondaryButton onClick={() => onClickAdmin(admin ? 'revoke' : 'grant')}>
                    {admin ? 'Revoke Admin' : 'Grant Admin'}
                  </Components.SecondaryButton>
                </Components.CheckboxFieldWrapper>
                <Components.CheckboxFieldWrapper>
                  <Components.Checkbox checked={tester || false} disabled />
                  <Components.CheckboxLabel>Tester</Components.CheckboxLabel>
                  <Components.SecondaryButton onClick={() => onClickTester(tester ? 'revoke' : 'grant')}>
                    {tester ? 'Revoke Tester' : 'Grant Tester'}
                  </Components.SecondaryButton>
                </Components.CheckboxFieldWrapper>
              </>
            </Components.FieldWrapper>

            <Components.FieldWrapper>
              <Components.SubsectionTitle>Force alias (RevenueCat)</Components.SubsectionTitle>
              <ProjectTextField {...forcedAliasProps} placeholder={'Enter the alias you want to link to this user.'} />
            </Components.FieldWrapper>

            <Components.FieldWrapper>
              <Components.SubsectionTitle>Gifts</Components.SubsectionTitle>
              {gifts.length === 0 ? (
                <Components.FieldBasic>There are no gifts associated with this profile.</Components.FieldBasic>
              ) : (
                <ProjectTable
                  data={giftsWithContextNames}
                  columns={tableColumns.profileGifts({
                    onRemove: (rowId) => {
                      onRemoveGift(rowId);
                    },
                  })}
                />
              )}
              <Components.SecondaryButton onClick={() => onClickAddGift()}>Add New Gift</Components.SecondaryButton>
            </Components.FieldWrapper>

            <Components.FieldWrapper>
              <Components.SubsectionTitle>Initial Survey</Components.SubsectionTitle>
              {!initialSurvey || initialSurvey === '' ? (
                <Components.FieldBasic>There is no initial survey on this profile.</Components.FieldBasic>
              ) : (
                <>
                  <UserProfileField
                    displayName={''}
                    fieldType={UserProfileFieldTypes.list}
                    data={initialSurvey?.length ? [initialSurvey] : []}
                    extraData={'Initial Survey ID'}
                  />
                  <Components.SecondaryButton destructive={true} onClick={onResetSurveys}>
                    Reset Surveys
                  </Components.SecondaryButton>
                </>
              )}
            </Components.FieldWrapper>

            <Components.FieldWrapper>
              <Components.SubsectionTitle>Learning Paths</Components.SubsectionTitle>
              {learningPaths.length === 0 ? (
                <Components.FieldBasic>There are no learning paths on this profile.</Components.FieldBasic>
              ) : (
                <>
                  <UserProfileField
                    displayName={''}
                    fieldType={UserProfileFieldTypes.list}
                    data={learningPaths}
                    extraData={'Learning Path ID'}
                  />

                  <Components.SecondaryButton destructive={true} onClick={onResetLearningPaths}>
                    Reset Learning Paths
                  </Components.SecondaryButton>
                </>
              )}
            </Components.FieldWrapper>

            <Components.FormButtonsContainer>
              <Components.PrimaryButton onClick={() => _onSave()}>Save Changes</Components.PrimaryButton>
            </Components.FormButtonsContainer>
          </>
        )}
        {!!user && (
          <>
            <Components.Separator />
            <Components.Header>Purchases (read-only)</Components.Header>

            <ProjectTable
              data={purchases || []}
              columns={tableColumns.purchases}
              customPageSize={25}
              sortingModel={{ field: 'createdAt', sort: 'desc' }}
            />

            <Components.Separator />
            <Components.Header>Transactions (read-only)</Components.Header>

            <ProjectTable
              data={transactions || []}
              columns={tableColumns.transactions}
              customPageSize={10}
              sortingModel={{ field: 'createdAt', sort: 'desc' }}
            />
          </>
        )}

        {!!userFields?.readOnly && userFields?.readOnly?.length > 0 && (
          <>
            <Components.Separator />
            <Components.Header>Full profile (read-only)</Components.Header>
          </>
        )}
        {userFields?.readOnly?.map((fieldData) => (
          <UserProfileField
            key={fieldData.displayName}
            displayName={fieldData.displayName}
            fieldType={fieldData.fieldType}
            data={fieldData.data}
            extraData={fieldData.extraData}
          />
        ))}
        <CreateDealModal {...createDealModalProps} />
        <SubscriptionInfoModal {...subscriptionInfoModal} />
        <ConfirmationModal
          {...confirmationModal}
          confirmationText={confirmationTexts.users.forceSyncWithRevenueCat}
          onConfirm={onForceSyncWithRevenueCat}
        />
        <ConfirmationModal
          {...confirmationModalDeleteAccount}
          confirmationText={confirmationTexts.users.deleteAccount}
          onConfirm={onDeleteAccount}
        />
        <ConfirmationModal
          {...confirmationModalImpersonateUser}
          confirmationText={confirmationTexts.users.impersonateUser}
          onConfirm={onImpersonateUser}
        />
        <ConfirmationModal
          {...confirmationModalDeleteSurveysAndLearningPaths}
          confirmationText={confirmationTexts.users.deleteSurveysAndLearningPaths}
          onConfirm={onDeleteSurveysAndLearningPaths}
        />
        <ConfirmationModal
          {...confirmationModalCopyUserData}
          modalTitle={'Copy user data'}
          confirmationText={confirmationTexts.users.copyUserData}
          detailsText={confirmationTexts.users.copyUserDataDetails}
          onConfirm={onCopyUserData}
        />
        <ConfirmationModal
          {...confirmationModalSendPasswordResetEmail}
          modalTitle={'Send password reset email'}
          confirmationText={confirmationTexts.users.sendPasswordResetEmail}
          onConfirm={onSendPasswordResetEmail}
        />
      </Components.Container>
    </ProjectContainer>
  );
};

export default UserLookup;
